import { createFileRoute, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { useAuth } from '@/lib/auth'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'

// ─── Demo credentials ─────────────────────────────────────────────────────────
// FOR DEMO USE ONLY — remove before production

const DEMO_USERS = [
  { email: 'admin@medicinpriser.dk', password: 'admin', role: 'admin' as const },
  { email: 'bruger@medicinpriser.dk', password: 'bruger', role: 'manager' as const },
]

function makeDemoToken(role: string): string {
  const payload = btoa(JSON.stringify({ role, exp: 9_999_999_999 }))
  return `eyJhbGciOiJub25lIn0.${payload}.demo`
}

// ─── Route ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>) => ({
    redirect: (s.redirect as string | undefined),
  }),
  component: LoginPage,
})

function LoginPage() {
  const { setToken } = useAuth()
  const { redirect } = useSearch({ from: '/login' })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const user = DEMO_USERS.find(
      u => u.email === email.trim().toLowerCase() && u.password === password,
    )

    if (!user) {
      setLoading(false)
      setError('Forkert e-mail eller adgangskode.')
      return
    }

    const token = makeDemoToken(user.role)
    setToken(token, user.email)
    window.location.replace(redirect ?? '/')
  }

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      {/* Left panel */}
      <div className="hidden md:flex flex-col justify-between bg-[#0f1117] p-10">
        <p className="font-mono text-xs tracking-widest text-[#4a4f63] uppercase">
          Medicinpriser
        </p>
        <p className="text-[#e8eaf0] text-2xl font-light leading-snug">
          Internationale lægemiddelpriser,{' '}
          <span className="font-medium text-white">altid ved hånden.</span>
        </p>
        <p className="font-mono text-xs text-[#2e3241]">© 2026 Medicinpriser</p>
      </div>

      {/* Right panel */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-6">
          <div>
            <h1 className="text-xl font-medium text-foreground">Log ind</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Indtast dine oplysninger for at fortsætte
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                E-mail
              </Label>
              <Input
                type="email"
                placeholder="du@eksempel.dk"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                Adgangskode
              </Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Logger ind…' : 'Log ind →'}
            </Button>
          </form>

          {/* Demo hint */}
          <div className="rounded-md border border-dashed border-border bg-muted/40 px-4 py-3 space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Demo-konti
            </p>
            {DEMO_USERS.map(u => (
              <button
                key={u.email}
                type="button"
                onClick={() => { setEmail(u.email); setPassword(u.password) }}
                className="w-full text-left text-xs text-muted-foreground hover:text-foreground transition-colors py-0.5"
              >
                <span className="font-mono">{u.email}</span>
                <span className="text-muted-foreground/60"> · {u.password}</span>
                <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground/50">
                  ({u.role})
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
